/**
 * Chart palette.
 *
 * Both columns are the validated categorical order (8 slots, fixed, never
 * cycled): worst adjacent CVD ΔE 9.1 light / 8.4 dark, normal-vision 19.6 /
 * 19.3. Three light slots sit below 3:1 against the light surface, so charts
 * always ship a legend and a table view as relief.
 *
 * Hues are assigned by the entity's position in a stable order, not by its rank
 * in the current filter, so hiding a series never repaints the others.
 */

export const CATEGORICAL_LIGHT = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#6250d6', // violet
  '#e34948', // red
] as const;

export const CATEGORICAL_DARK = [
  '#3987e5',
  '#d95926',
  '#199e70',
  '#c98500',
  '#d55181',
  '#008300',
  '#9085e9',
  '#e66767',
] as const;

/** Past eight entities, fold the rest together rather than inventing hues. */
export const MAX_SERIES = 8;

export interface ChartTheme {
  dark: boolean;
  categorical: readonly string[];
  surface: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  grid: string;
}

export function readChartTheme(element: HTMLElement | null): ChartTheme {
  const dark =
    document.documentElement.dataset.theme === 'dark' ||
    (document.documentElement.dataset.theme !== 'light' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);
  const styles = element ? getComputedStyle(element) : null;
  const token = (name: string, fallback: string) =>
    styles?.getPropertyValue(name).trim() || fallback;

  return {
    dark,
    categorical: dark ? CATEGORICAL_DARK : CATEGORICAL_LIGHT,
    surface: token('--surface-1', dark ? '#1a1a19' : '#fcfcfb'),
    textPrimary: token('--text-primary', dark ? '#f0efec' : '#0b0b0b'),
    textSecondary: token('--text-secondary', dark ? '#c3c2b7' : '#52514e'),
    textMuted: token('--text-muted', dark ? '#8b897e' : '#75736c'),
    grid: token('--border-subtle', dark ? '#2e2e2c' : '#e7e6e1'),
  };
}
